import { Component, Prop, h, State, Watch, Build, Element } from '@stencil/core';
import { assetUrl } from '../../../../../utils/helpers/asset-path';

const COMPONENT_NAME = 'dh-icon';

/**
 * Icon component for displaying SVG icons with lazy loading and SSR inline support.
 * Icons are organized in categories using the format: 'category--name'.
 * For example: 'interface--close' will load from '/assets/icons/interface/close.svg'.
 *
 * Supports three input modes:
 * - **Name-based:** `name="arrows--chevron-right"` → resolves to asset path
 * - **URL-based:** `name="https://cdn.example.com/icon.svg"` → fetches from URL
 * - **Inline SVG:** `name="<svg>...</svg>"` → renders directly
 *
 * **SSR contract:** When `inline="true"`, the component renders a placeholder with
 * `data-inline-icon` attribute. The server-side post-processor resolves the SVG
 * (by CDN base path + predictable structure `/assets/icons/{category}/{name}.svg`
 * or by absolute URL) and inlines it, adding `data-inlined` attribute.
 * On the client, if `data-inlined` is present, the component renders a `<slot/>`
 * to preserve the server-injected content.
 *
 * @example
 *   <dh-icon name="interface--check"></dh-icon>
 */
@Component({
  tag: 'dh-icon',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhIcon {
  @Element() host!: HTMLElement;

  private observer: IntersectionObserver;
  private iconContainer?: HTMLDivElement;
  private iconLoading = false;

  /**
   * Name of the icon to display.
   * Use format 'category--name' for categorized icons (e.g., 'interface--close')
   * or just 'name' for icons in the root directory.
   * Also supports inline SVG strings (e.g., '<svg>...</svg>') or CDN URLs (e.g., 'https://...').
   */
  @Prop({ reflect: true }) name!: string;

  /**
   * Optional color override for the icon.
   * Applies to the SVG fill property via inline style.
   */
  @Prop() color?: string;

  /**
   * Enable lazy loading (default: true).
   * When true, icons load only when near viewport. When false, loads immediately.
   */
  @Prop() lazy: boolean = true;

  /**
   * Inline the SVG during server-side rendering (SSR).
   * When true, the component renders a placeholder with `data-inline-icon` attribute
   * for the server-side post-processor to resolve and inline the SVG.
   * On client-side, behaves like lazy="false" (loads immediately).
   * @default false
   */
  @Prop({ reflect: true }) inline: boolean = false;

  /**
   * Width of the icon container. Accepts any CSS unit (px, rem, %, etc.).
   * Numeric values without units will be treated as pixels.
   * Prevents CLS by reserving space before icon loads.
   */
  @Prop() width?: string;

  /**
   * Height of the icon container. Accepts any CSS unit (px, rem, %, etc.).
   * Numeric values without units will be treated as pixels.
   * Prevents CLS by reserving space before icon loads.
   */
  @Prop() height?: string;

  /**
   * Additional CSS class names to apply to the icon container.
   */
  @Prop() class?: string;

  @State() svgContent: string = '';
  @State() iconType: 'svg' | 'url' | 'name' = 'name';

  /**
   * Whether lazy loading is effectively disabled.
   */
  private get isLazy(): boolean {
    if (this.inline) return false;
    return this.lazy;
  }

  private normalizeSize(value?: string): string | undefined {
    if (!value) return undefined;
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue) && value.trim() === String(numericValue)) {
      return `${numericValue}px`;
    }
    return value;
  }

  private detectIconType(): 'svg' | 'url' | 'name' {
    if (!this.name) return 'name';
    const trimmed = this.name.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return 'url';
    if (trimmed.startsWith('<svg') || trimmed.startsWith('<SVG') || /<svg[\s>]/i.test(trimmed)) return 'svg';
    return 'name';
  }

  async componentWillLoad() {
    const detectedType = this.detectIconType();
    if (detectedType !== this.iconType) {
      this.iconType = detectedType;
    }

    if (detectedType === 'svg') {
      this.svgContent = this.processInlineSvg(this.name.trim());
    }
  }

  componentDidLoad() {
    // If server already inlined the icon, nothing to do
    if (this.host.hasAttribute('data-inlined')) return;

    // Inline SVG and URL icons are already processed
    if (this.iconType === 'svg' || this.iconType === 'url') return;

    if (!this.isLazy) {
      this.loadIcon();
    } else {
      this.setupIntersectionObserver();
    }
  }

  componentDidRender() {
    if (this.color && this.svgContent && this.iconContainer) {
      this.updateIconColor();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  @Watch('name')
  watchNameHandler() {
    const newIconType = this.detectIconType();
    if (newIconType !== this.iconType) {
      this.iconType = newIconType;
    }
    if (this.iconType === 'svg') {
      this.svgContent = this.processInlineSvg(this.name.trim());
    } else {
      this.svgContent = '';
      this.iconLoading = false;
      this.loadIcon();
    }
  }

  @Watch('color')
  watchColorHandler() {
    requestAnimationFrame(() => {
      if (this.svgContent && this.iconContainer) {
        this.updateIconColor();
      }
    });
  }

  /**
   * Parses the icon name to extract category and file name.
   * Supports multi-level directories using -- as separator.
   * 'arrows--chevron-right' → /assets/icons/arrows/chevron-right.svg
   * 'dochub--g2-crowd' → /assets/icons/dochub/g2-crowd.svg
   */
  private getIconPath(): string {
    const parts = this.name.split('--');
    if (parts.length > 1) {
      const dir = parts.slice(0, -1).join('/');
      const file = parts[parts.length - 1];
      return assetUrl(`icons/${dir}/${file}.svg`);
    }
    return assetUrl(`icons/${this.name}.svg`);
  }

  /**
   * Processes inline SVG: removes standard fill attributes for currentColor inheritance,
   * preserves specific fill colors and marks them with data-no-inherit.
   */
  private processInlineSvg(svgString: string): string {
    const standardColors = ['black', 'white', 'currentcolor', 'none'];
    const fillColorPattern = /fill=["']([^"']+)["']/gi;
    const allMatches = [...svgString.matchAll(fillColorPattern)];

    if (allMatches.length > 0) {
      const hasSpecificColors = allMatches.some(match => {
        const color = match[1]?.toLowerCase();
        if (!color) return false;
        if (standardColors.includes(color)) return false;
        return color.startsWith('#') || /^[a-zA-Z]{3,}$/.test(color);
      });

      if (hasSpecificColors) {
        return svgString.replace(/<svg([^>]*)>/i, (match, attrs) => {
          if (/data-no-inherit/.test(attrs)) return match;
          return `<svg${attrs} data-no-inherit="true">`;
        });
      }

      const usesStroke = /\bstroke\s*=/.test(svgString);
      let processed = svgString;
      standardColors.forEach(color => {
        if (color === 'none' && usesStroke) return;
        const pattern = new RegExp(`fill=["']${color}["']`, 'gi');
        processed = processed.replace(pattern, '');
      });

      if (!usesStroke) {
        processed = processed.replace(/<svg([^>]*)>/i, '<svg$1 fill="currentColor">');
      }
      return processed;
    }

    return svgString;
  }

  private setupIntersectionObserver() {
    if (!Build.isBrowser || typeof IntersectionObserver === 'undefined') return;
    if (this.iconType !== 'name' || !this.iconContainer) return;

    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          this.loadIcon();
          this.observer?.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    this.observer.observe(this.iconContainer);

    requestAnimationFrame(() => {
      if (!this.iconContainer || !this.observer) return;
      const rect = this.iconContainer.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0) {
        this.loadIcon();
        this.observer?.disconnect();
      }
    });
  }

  private async loadIcon() {
    if (!this.name) return;
    if (this.iconLoading || this.svgContent) return;
    this.iconLoading = true;

    if (!Build.isBrowser || typeof window === 'undefined' || typeof fetch === 'undefined') {
      this.iconLoading = false;
      return;
    }

    const iconPath = this.getIconPath();

    try {
      const response = await fetch(iconPath);
      if (!response.ok) throw new Error(`Failed to load icon: ${this.name} at path: ${iconPath}`);

      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('svg') && !contentType.includes('xml') && !contentType.includes('text')) {
        console.warn(`${COMPONENT_NAME}: Expected SVG but got ${contentType} for icon: ${this.name}`);
        return;
      }

      let svgText = await response.text();

      if (!svgText.trim().startsWith('<svg') && !svgText.includes('<svg')) {
        console.warn(`${COMPONENT_NAME}: Invalid SVG content for icon: ${this.name}`);
        return;
      }

      svgText = this.processInlineSvg(svgText);
      this.svgContent = svgText;
    } catch (error) {
      if (Build.isBrowser) {
        console.error('Error loading icon:', error);
      }
    } finally {
      this.iconLoading = false;
    }
  }

  private updateIconColor() {
    if (!this.color || !this.iconContainer) return;
    const svg = this.iconContainer.querySelector('svg');
    if (svg && !svg.hasAttribute('data-no-inherit')) {
      svg.style.fill = this.color;
    }
  }

  private getNumericDimension(value?: string): number | undefined {
    if (!value) return undefined;
    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? undefined : Math.round(numericValue);
  }

  render() {
    const inlineStyles: { [key: string]: string } = {};
    const normalizedWidth = this.normalizeSize(this.width);
    const normalizedHeight = this.normalizeSize(this.height);
    if (normalizedWidth) inlineStyles.width = normalizedWidth;
    if (normalizedHeight) inlineStyles.height = normalizedHeight;
    const hasInlineStyles = Object.keys(inlineStyles).length > 0;

    // SSR: server already inlined the icon — preserve via slot
    if (this.host.hasAttribute('data-inlined')) {
      return <slot />;
    }

    // SSR contract: render placeholder for server post-processor
    // The server will find [data-inline-icon], resolve SVG, inline it, add data-inlined
    if (this.inline && !Build.isBrowser) {
      const iconRef = this.detectIconType() === 'url' ? this.name.trim() : this.name;
      return (
        <div
          class={`${COMPONENT_NAME} ${this.class || ''}`.trim()}
          style={hasInlineStyles ? inlineStyles : undefined}
          data-inline-icon={iconRef}
        >
          {/* ssr:icon */}
        </div>
      );
    }

    // URL-based icon → <img>
    if (this.iconType === 'url') {
      return (
        <div
          ref={el => (this.iconContainer = el as HTMLDivElement)}
          class={`${COMPONENT_NAME} ${this.class || ''}`.trim()}
          style={hasInlineStyles ? inlineStyles : undefined}
        >
          <img
            src={this.name.trim()}
            loading={this.isLazy ? 'lazy' : undefined}
            width={this.getNumericDimension(this.width)}
            height={this.getNumericDimension(this.height)}
            alt=""
          />
        </div>
      );
    }

    // SVG content (inline string or fetched)
    return (
      <div
        ref={el => (this.iconContainer = el as HTMLDivElement)}
        class={`${COMPONENT_NAME} ${this.class || ''}`.trim()}
        style={hasInlineStyles ? inlineStyles : undefined}
        innerHTML={this.svgContent}
      />
    );
  }
}
